import { JSONSchema9, JSONSchema9Definition } from "../types/jsonSchema";
import { templateObjectsService } from "@src/lib/schemas/TemplateObjectsService";
import { deref } from "../walter";
import { captureErrorInfo } from "@src/taskpane/helpers/errorHandler";

type PathSegment = { key: string } | { isArray: true };

/**
 * This class provides a simple interface for working with JSON Schemas containing $refs.
 * It allows you to find properties in a schema by path without the complexity of managing and resolving $refs.
 */
export abstract class SchemaContext {
  static get defaultDefinitions(): JSONSchema9["definitions"] {
    const schema = templateObjectsService.getSchemaSync();
    if (schema?.definitions) {
      return schema.definitions;
    } else {
      captureErrorInfo(new Error("No definitions found in schema"));
      return {};
    }
  }

  static parsePath(path: string): PathSegment[] {
    const unparsedSegments = path.split(".");

    return unparsedSegments.reduce((acc: PathSegment[], segment) => {
      const isArray = segment.endsWith("[]");
      const key = isArray ? segment.slice(0, -2) : segment;

      if (key) acc.push({ key });
      if (isArray) acc.push({ isArray });
      return acc;
    }, []);
  }

  static joinPathSegments(segments: PathSegment[]): string {
    return segments.reduce((path, segment) => {
      if ("isArray" in segment) {
        return `${path}[]`;
      } else {
        return [path, segment.key].filter(Boolean).join(".");
      }
    }, "");
  }

  static parentPath(path: string): string | null {
    if (path === "") return null;

    const segments = this.parsePath(path);
    return this.joinPathSegments(segments.slice(0, -1));
  }

  static findSchema(path: string, rootSchema: JSONSchema9): JSONSchema9 | null {
    const pathSegments = SchemaContext.parsePath(path);
    let currentSchema: JSONSchema9Definition | JSONSchema9Definition[] = rootSchema;

    for (const pathSegment of pathSegments) {
      // We can't lookup properties in boolean schemas
      if (currentSchema === false || currentSchema === true) return null;

      // We don't support lookups into tuple validations yet
      // https://json-schema.org/understanding-json-schema/reference/array#tuple-validation
      if (Array.isArray(currentSchema)) return null;

      currentSchema = this.resolveSchema(currentSchema, rootSchema.definitions);

      if ("isArray" in pathSegment) {
        if (currentSchema.type === "array" && currentSchema.items) {
          currentSchema = currentSchema.items;
        } else {
          // Can't find the array items, return null
          return null;
        }
      } else {
        // look for object properties
        if (currentSchema.type === "object" && currentSchema.properties && currentSchema.properties[pathSegment.key]) {
          currentSchema = currentSchema.properties[pathSegment.key];
        } else {
          // Can't find the property, return null
          return null;
        }
      }
    }

    // We can't lookup properties in boolean schemas
    if (currentSchema === false || currentSchema === true) return null;

    // We don't support lookups into tuple validations yet
    // https://json-schema.org/understanding-json-schema/reference/array#tuple-validation
    if (Array.isArray(currentSchema)) return null;

    return currentSchema;
  }
  static resolveSchema<T extends JSONSchema9>(
    currentSchema: T,
    additionalDefinitions: JSONSchema9["definitions"] = {},
    options?: {
      recursive?: boolean;
    },
  ): T {
    const { recursive } = { recursive: false, ...options };

    while (currentSchema.$ref) {
      const definition = deref(currentSchema.$ref, additionalDefinitions);

      if (definition && typeof definition === "object") {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- using destructuring to remove $ref
        const { $ref, ...restOfSchema } = currentSchema;
        currentSchema = { ...definition, ...restOfSchema } as T;
      } else {
        // Can't find the definition, return the current schema
        break;
      }

      // Optionally, keep resolving $refs until we can't find any more
      // This allows a property to point to a definition that points to another definition.
      if (!recursive) break;
    }

    return currentSchema;
  }

  /**
   * Recursively resolves schema references and collects all needed definitions. This function:
   * - Accepts a JSON schema and optional definitions map
   * - Traverses the entire schema structure (properties, items, allOf, etc.)
   * - Identifies all $ref references and extracts their corresponding definitions
   * - Returns a new schema with all referenced definitions collected in a definitions object
   */
  static resolveSchemaRecursive<T extends JSONSchema9>(currentSchema: T): T {
    const collectedDefinitions: JSONSchema9["definitions"] = {};

    // Helper function to recursively walk the schema
    const walkSchema = (schema: JSONSchema9Definition): void => {
      if (typeof schema === "boolean") return;

      // If we find a $ref, add its definition to our collection
      if (schema.$ref) {
        const definition = deref(schema.$ref);

        if (definition && typeof definition === "object") {
          const definitionName = schema.$ref.replace(/^#\/definitions\//, "");
          collectedDefinitions[definitionName] = definition;
          // Walk the definition to collect nested refs
          walkSchema(definition);
        } else {
          throw new Error(`Definition ${schema.$ref} not found!`);
        }
      }

      // Walk through object properties
      if (schema.properties) {
        Object.values(schema.properties).forEach(walkSchema);
      }

      // Walk through array items
      if (schema.items) {
        if (Array.isArray(schema.items)) {
          schema.items.forEach(walkSchema);
        } else {
          walkSchema(schema.items);
        }
      }

      // Walk through allOf, anyOf, oneOf
      ["allOf", "anyOf", "oneOf"].forEach((key) => {
        const schemas = schema[key as keyof JSONSchema9] as JSONSchema9Definition[] | undefined;
        if (schemas) {
          schemas.forEach(walkSchema);
        }
      });
    };

    // Start the walk
    walkSchema(currentSchema);

    // Return a new schema with collected definitions
    return {
      ...currentSchema,
      definitions: Object.keys(collectedDefinitions).length > 0 ? collectedDefinitions : undefined,
    } as T;
  }
}
